import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { AdminService } from '../../../services/admin.service';
import { UserService } from '../../../services/user.service';
import { saveAs } from 'file-saver/FileSaver';
import { ApiConnectService } from '../../../services/api-connect.service';
import { LanguageSwitcher } from '../../../header/language-switcher/language-switcher.model';
import { ViewDocxComponent } from '../../views/view-docx/view-docx.component';
import { ViewDocxService } from '../../../services/view-docx.service';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDelete } from '../../../modals/confirmDelete/confirmDelete.component';
import { StepsControllerService } from '../../../services/steps-controller.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { StandardCatalogItem, StandardCatalogPage } from '../../../services/admin.service.types';
import { StandardPinScope } from '../../../models/standard-pin.model';
import { readStandardViewPage, saveStandardViewPage, clearStandardViewPage } from '../../../services/standard-list-navigation';
import { Subscription } from 'rxjs/Subscription';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

const FIRST_PAGE = 1;
const HTTP_OK = 200;
const HTTP_NO_CONTENT = 204;
const NO_ITEMS = 0;
const NAME_SEPARATOR = ', ';
const DEPARTMENT_ROUTE_PARAMETER = 'id';
const SCOPE_TOOLTIP_MARGIN = 12;
const SCOPE_TOOLTIP_GAP = 8;
const SCOPE_TOOLTIP_CLASS = 'standard-scope-tooltip-window';
const DESCRIBED_BY_ATTRIBUTE = 'aria-describedby';
const SCOPE_TOOLTIP_LEFT = '--standard-scope-left';
const SCOPE_TOOLTIP_TOP = '--standard-scope-top';
const PIXELS = 'px';


@Component({
	selector: 'app-all-standards',
	templateUrl: './all-standards.component.html',
	styleUrls: ['./all-standards.component.css']
})
export class AllStandardsComponent implements OnInit, OnDestroy {
	pinnedOnly = false;
	private activeScopeTooltip: NgbTooltip;
	private routeSubscription: Subscription;
	private readonly catalogChange$ = new Subject<void>();
	noStandardsInfo = true;
	getStandardErrorInfo = false;
	isLoaded = false;
	allStandardList: StandardCatalogItem[] = [];
	sortDirection = true; // false;
	standardNotFound = false;
	errorMessage = false;
	currentSearchValue = '';
	currentPage = 0;
	currentStatus = '';
	clearFilterStatus = '';
	p = readStandardViewPage();
	totalItems = 0; // totalItems for ngx pagination
	currentSortId = 'number,asc';

	statusList = ['ACTIVE', 'NEW', 'INACTIVE', 'UNDER_CONFIRMATION', 'UNDER_FAMILIARIZATION', this.clearFilterStatus];

	constructor(private adminService: AdminService,
		private userService: UserService,
		private modalService: NgbModal,
		public viewDocxService: ViewDocxService,
		private apiConnectService: ApiConnectService,
		public languageSwitcher: LanguageSwitcher,
		public stepsControllerService: StepsControllerService,
		private route: ActivatedRoute,
		public router: Router) {
	}

	ngOnInit() {
        this.currentStatus = this.clearFilterStatus;
        this.routeSubscription = this.route.params.subscribe(params => {
            this.pinnedOnly = false;
            if (params[DEPARTMENT_ROUTE_PARAMETER]) {
                this.getAllStandardsAPIById(params[DEPARTMENT_ROUTE_PARAMETER]);
                return;
            }
            this.getAllStandardsAPI();
        });
    }

    get generalCatalog(): boolean { return !this.route.snapshot.params[DEPARTMENT_ROUTE_PARAMETER]; }

    changePinnedOnly(): void {
        this.p = FIRST_PAGE;
        this.getPageAPI(FIRST_PAGE);
    }

    trackScope(index: number, scope: StandardPinScope): string { return scope.scopeId; }

    trackDistribution(index: number, value: {scope: StandardPinScope}): string { return value.scope.scopeId; }

    positionScopeTooltip(trigger: HTMLElement, tooltip: NgbTooltip, pointer?: MouseEvent): void {
        if (this.activeScopeTooltip && this.activeScopeTooltip !== tooltip) { this.activeScopeTooltip.close(); }
        this.activeScopeTooltip = tooltip;
        tooltip.open();
        const panel = trigger.ownerDocument.getElementById(trigger.getAttribute(DESCRIBED_BY_ATTRIBUTE));
        if (!panel) { return; }
        panel.classList.add(SCOPE_TOOLTIP_CLASS);
        this.alignScopeTooltip(trigger, panel, pointer);
        trigger.ownerDocument.defaultView.requestAnimationFrame(() => {
            if (panel.parentNode && this.activeScopeTooltip === tooltip) { this.alignScopeTooltip(trigger, panel, pointer); }
        });
    }

    private alignScopeTooltip(trigger: HTMLElement, panel: HTMLElement, pointer?: MouseEvent): void {
        const anchor = trigger.getBoundingClientRect();
        const bounds = panel.getBoundingClientRect();
        const viewport = trigger.ownerDocument.documentElement;
        const desiredLeft = (pointer ? pointer.clientX : anchor.right) + SCOPE_TOOLTIP_GAP;
        const desiredTop = pointer ? pointer.clientY + SCOPE_TOOLTIP_GAP : anchor.top;
        panel.style.setProperty(SCOPE_TOOLTIP_LEFT, Math.max(SCOPE_TOOLTIP_MARGIN,
            Math.min(desiredLeft, viewport.clientWidth - bounds.width - SCOPE_TOOLTIP_MARGIN)) + PIXELS);
        panel.style.setProperty(SCOPE_TOOLTIP_TOP, Math.max(SCOPE_TOOLTIP_MARGIN,
            Math.min(desiredTop, viewport.clientHeight - bounds.height - SCOPE_TOOLTIP_MARGIN)) + PIXELS);
    }

    @HostListener('window:resize')
    @HostListener('window:scroll')
    @HostListener('document:keydown.escape')
    closeScopeTooltip(): void {
        if (this.activeScopeTooltip) { this.activeScopeTooltip.close(); this.activeScopeTooltip = null; }
    }

    ngOnDestroy(): void {
        this.closeScopeTooltip();
        this.catalogChange$.next();
        this.catalogChange$.complete();
        if (this.routeSubscription) { this.routeSubscription.unsubscribe(); }
        clearStandardViewPage();
    }

    getAllStandardsAPI(): void { this.loadCatalog(this.p); }

    getAllStandardsAPIById(id: string): void {
        this.loadResponse(this.adminService.getAllStandardsById(id) as Observable<HttpResponse<StandardCatalogPage>>);
    }

    searchValue(value: string): void {
        this.currentStatus = this.clearFilterStatus;
        this.currentSearchValue = value;
        this.loadCatalog(FIRST_PAGE);
    }

    private loadCatalog(pageNumber: number): void {
        this.p = pageNumber;
        saveStandardViewPage(pageNumber);
        const query = {page: pageNumber - FIRST_PAGE, value: this.currentSearchValue.trim(),
            status: this.currentStatus, sort: this.currentSortId, pinnedOnly: this.generalCatalog && this.pinnedOnly};
        if (!this.generalCatalog) {
            this.loadResponse(this.adminService.getPageServera(this.route.snapshot.params[DEPARTMENT_ROUTE_PARAMETER],
                query.page, query.value, query.status, query.sort) as Observable<HttpResponse<StandardCatalogPage>>);
            return;
        }
        this.loadResponse(this.adminService.getStandardCatalog(query));
    }

    private loadResponse(request: Observable<HttpResponse<StandardCatalogPage>>): void {
        this.catalogChange$.next();
        request.takeUntil(this.catalogChange$).subscribe(response => this.acceptPage(response), () => {
            this.getStandardErrorInfo = true;
            this.isLoaded = true;
        });
    }

    private acceptPage(response: HttpResponse<StandardCatalogPage>): void {
        if (!response || response.status !== HTTP_OK && response.status !== HTTP_NO_CONTENT
            || response.status === HTTP_OK && (!response.body || !Array.isArray(response.body.content))) {
            this.handleStandardOpenFailed();
            return;
        }
        this.allStandardList = response.body ? response.body.content : [];
        this.allStandardList.forEach(item => item.pinnedScopeNames =
            (item.pinnedScopes || []).map(scope => scope.name).join(NAME_SEPARATOR));
        this.totalItems = response.body ? response.body.totalElements : NO_ITEMS;
        this.noStandardsInfo = this.allStandardList.length === NO_ITEMS;
        this.standardNotFound = this.noStandardsInfo;
        this.getStandardErrorInfo = false;
        this.isLoaded = true;
    }

	editThisStandard(standardID: string, event: Event): void {
		event.preventDefault();
		if (!standardID) {
			return;
		}
		this.setTempStandardForEditing(standardID);
	}

	setTempStandardForEditing(id: string): void {
		this.adminService.getTempStandard(id).subscribe(
			(res: any) => {
				if (!res || res.status !== 200 || !res.body) {
					this.handleStandardOpenFailed();
					return;
				}
				const tmpStnd = res.body;
				tmpStnd.status = "ON_EDITING";
				this.stepsControllerService.activeStep = 8;
				this.stepsControllerService.setTempParrentID(id);
				this.stepsControllerService.setCurrentStandard(tmpStnd);
			},
			() => this.handleStandardOpenFailed()
		);
	}

	private handleStandardOpenFailed(): void {
		this.getStandardErrorInfo = true;
		this.isLoaded = true;
	}

	// FILTER LIST BY STATUS
	filterByStatus(status) {
		if (status === this.clearFilterStatus) {
			this.clearAllFilter();
		} else {
			this.currentStatus = status;
			this.getFilteredStatus();
		}
	}


    getFilteredStatus(): void { this.loadCatalog(FIRST_PAGE); }

	clearAllFilter() {
		this.currentStatus = this.clearFilterStatus;
		this.getFilteredStatus();
	}

	// SORT LIST BY sortId // // // // // //
	// sortId values:
	// 'number, asc' is using by default
	// 'number,asc' or 'number,desc'
	// 'ruName,asc' or 'ruName,desc'
	// 'enName,asc' or 'enName,desc'
	// 'ruType,asc' or 'ruType,desc'
	// 'enType,asc' or 'enType,desc'
	// 'status,asc' or 'status,desc'
	// // // // // // // // // // // // //
	sortList(sortId) {

		if (this.sortDirection) {
			sortId += ',desc';
			this.sortDirection = !this.sortDirection;
			this.currentSortId = sortId;
		} else {
			sortId += ',asc';
			this.sortDirection = !this.sortDirection;
			this.currentSortId = sortId;
		}
		this.getSortedList(sortId);

	}

    getSortedList(sortId: string): void {
        this.currentSortId = sortId;
        this.loadCatalog(this.p);
    }


	openDocModal(docxID, sID) {

		localStorage.setItem('DocxID', docxID);
		localStorage.setItem('DocxSID', sID);

		const modalRef = this.modalService.open(ViewDocxComponent, { size: 'lg', backdrop: 'static' });
		modalRef.result.then((result) => {
			if (result === 'Close') {
				// on close button
				this.viewDocxService.deleteLinkByDocxID();
			}
		}, (reason) => {
			// on dismiss
			this.viewDocxService.deleteLinkByDocxID();
		});

	}

	openDocModalDOCX(fileLink) {
		localStorage.setItem('fileLink', fileLink);

		const modalRef = this.modalService.open(ViewDocxComponent, { size: 'lg', backdrop: 'static' });
		modalRef.result.then((result) => {
			if (result === 'Close') {
				// on close button
				this.viewDocxService.deleteLinkByDocxIDManually();
			}
		}, (reason) => {
			// on dismiss
			this.viewDocxService.deleteLinkByDocxIDManually();
		});
	}


	downloadFile(fileId: string) {
		this.userService.getFile(fileId).subscribe(
			(response: any) => {
				const link = document.createElement('a');
				link.href = window.URL.createObjectURL(response.body);
				link.download = response.headers.get('filename');
				link.click();
				link.remove();
			},
			() => {
			}
		);
	}

	openPdf(fileId: string) {
		window.open(this.apiConnectService.getPdf + fileId, '_blank');
	}

    getPage(page: number): void { this.loadCatalog(page); }

    getPageAPI(page: number): void { this.loadCatalog(page); }

    getPageAPII(page: number): void { this.loadCatalog(page); }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/all-standards/all-standards.component.ts